import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, X, Trash2, Power, PowerOff, Edit2, Building2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function Colegios() {
    const [schools, setSchools] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);

    async function load() {
        const s = await api.get("/ejecutivo/schools");
        setSchools(s.data || []);
    }
    useEffect(() => { load(); }, []);

    async function deactivate(id) {
        if (!window.confirm("¿Desactivar colegio? Los usuarios no podrán acceder.")) return;
        await api.post(`/ejecutivo/schools/${id}/deactivate`);
        toast.success("Colegio desactivado");
        load();
    }
    async function activate(id) {
        await api.post(`/ejecutivo/schools/${id}/activate`);
        toast.success("Colegio activado");
        load();
    }
    async function remove(id) {
        if (!window.confirm("⚠️ ELIMINAR PERMANENTEMENTE este colegio?\n\nLos usuarios serán desvinculados. Esta acción no se puede deshacer.")) return;
        await api.delete(`/ejecutivo/schools/${id}`);
        toast.success("Colegio eliminado");
        load();
    }

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="colegios-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold inline-flex items-center gap-2">
                        <Building2 size={26} className="text-edu-navy" /> Colegios
                    </h1>
                    <p className="text-edu-body">Administra colegios registrados.</p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2"
                    data-testid="create-school"
                >
                    <Plus size={14} /> Nuevo Colegio
                </button>
            </div>

            <div className="edu-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-edu-soft border-b border-edu">
                            <tr className="text-left">
                                <th className="px-4 py-3">Colegio</th>
                                <th className="px-4 py-3">Ubicación</th>
                                <th className="px-4 py-3">Capacidad</th>
                                <th className="px-4 py-3">Activos</th>
                                <th className="px-4 py-3">Estado</th>
                                <th className="px-4 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schools.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-6 text-edu-muted">
                                        Sin colegios registrados.
                                    </td>
                                </tr>
                            )}
                            {schools.map((s) => (
                                <tr key={s.id} className="border-b border-edu/50">
                                    <td className="px-4 py-3 font-medium">{s.name}</td>
                                    <td className="px-4 py-3 text-edu-body">
                                        {s.state}, {s.country}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-edu-muted">
                                        {s.num_students || 0} alm · {s.num_teachers || 0} prof · {s.num_admins || 0} adm
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {s.active_students}/{s.active_teachers}/{s.active_admins}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`edu-pill ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                                            {s.active ? "Activo" : "Inactivo"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <button
                                                onClick={() => setEditing(s)}
                                                className="px-2 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1"
                                                title="Editar"
                                            >
                                                <Edit2 size={12} />
                                            </button>
                                            {s.active ? (
                                                <button
                                                    onClick={() => deactivate(s.id)}
                                                    className="px-2 py-1 text-xs rounded-lg bg-yellow-100 text-yellow-800 inline-flex items-center gap-1"
                                                    title="Desactivar"
                                                >
                                                    <PowerOff size={12} />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => activate(s.id)}
                                                    className="px-2 py-1 text-xs rounded-lg bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"
                                                    title="Activar"
                                                >
                                                    <Power size={12} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => remove(s.id)}
                                                className="px-2 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"
                                                title="Eliminar permanentemente"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {(creating || editing) && (
                <SchoolModal
                    school={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function SchoolModal({ school, onClose, onSaved }) {
    const isEdit = !!school;
    const [form, setForm] = useState(
        school ?? {
            name: "",
            country: "Perú",
            state: "",
            num_students: 100,
            num_teachers: 10,
            num_admins: 2,
        }
    );
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!form.name || !form.country || !form.state) {
            toast.error("Nombre, país y estado son obligatorios");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                country: form.country,
                state: form.state,
                num_students: parseInt(form.num_students, 10),
                num_teachers: parseInt(form.num_teachers, 10),
                num_admins: parseInt(form.num_admins, 10),
            };
            if (isEdit) {
                await api.patch(`/ejecutivo/schools/${school.id}`, payload);
            } else {
                await api.post("/ejecutivo/schools", payload);
            }
            toast.success(isEdit ? "Colegio actualizado" : "Colegio creado");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    return createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" onClick={onClose}>
            <div className="edu-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">
                        {isEdit ? "Editar colegio" : "Nuevo colegio"}
                    </h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <Field label="Nombre del colegio *">
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="edu-input w-full"
                            placeholder="Ej: Colegio San Ignacio"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="País *">
                            <input
                                value={form.country}
                                onChange={(e) => setForm({ ...form, country: e.target.value })}
                                className="edu-input w-full"
                            />
                        </Field>
                        <Field label="Estado/Departamento *">
                            <input
                                value={form.state}
                                onChange={(e) => setForm({ ...form, state: e.target.value })}
                                className="edu-input w-full"
                                placeholder="Ej: Lima"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="N° estudiantes *">
                            <input
                                type="number"
                                min={0}
                                value={form.num_students}
                                onChange={(e) => setForm({ ...form, num_students: e.target.value })}
                                className="edu-input w-full"
                            />
                        </Field>
                        <Field label="N° profesores *">
                            <input
                                type="number"
                                min={0}
                                value={form.num_teachers}
                                onChange={(e) => setForm({ ...form, num_teachers: e.target.value })}
                                className="edu-input w-full"
                            />
                        </Field>
                        <Field label="N° admins *">
                            <input
                                type="number"
                                min={0}
                                value={form.num_admins}
                                onChange={(e) => setForm({ ...form, num_admins: e.target.value })}
                                className="edu-input w-full"
                            />
                        </Field>
                    </div>
                    <div className="flex justify-end gap-3 pt-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">
                            Cancelar
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="edu-btn-primary px-4 py-2 text-sm"
                        >
                            {saving ? "Guardando…" : isEdit ? "Guardar" : "Crear colegio"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                {label}
            </span>
            {children}
        </label>
    );
}
